import { ActionIcon, Button, Switch, Text, Tooltip } from "@mantine/core";
import { ChevronLeft, ChevronRight, Diamond, Lock, RefreshCw, Trash2 } from "lucide-react";
import { useId, useState, type ReactNode } from "react";

import { ErrorAlert } from "../../platform/ErrorAlert";
import { useStreamStatus } from "../../platform/stream-status";
import { ConfirmModal } from "../../ui/ConfirmModal";
import { AssistantPanel } from "./assistant/AssistantPanel";
import { useAssistChat } from "./assistant/use-assist-chat";
import { useAssistant, type AssistantEditor } from "./assistant/use-assistant";
import { BackgroundSection } from "./BackgroundParams";
import { BeatsSection } from "./BeatParams";

import { liveFrame, type Size } from "./engine/project";
import { onMac } from "./keys";
import type { SourceMedia } from "./media";
import { sourceKey } from "./media";
import type { AspectRatio } from "./project";
import { LevelSlider, ParamSection, SecondsField } from "./params";
import { usePlayhead, type Playhead } from "./playhead";
import { LookSections } from "./PictureParams";
import { EffectSettings } from "./EffectParams";
import { TextSettings } from "./TextParams";
import { TransitionSettings } from "./TransitionParams";
import type { Selection } from "./selection";
import { usePictureSize } from "./size";
import {
  MAX_FADE_MS,
  MAX_VOLUME,
  MIN_SPAN_MS,
  clipMs,
  volumeAt,
  volumeNeighbours,
  volumePointNear,
  clipsOn,
  junctionsOf,
  type AudioClip,
  type ClipPatch,
  type MediaClip,
  type Timeline,
  type TimelineEdit,
  type VideoClip,
} from "./timeline";
import { formatClock } from "./time";
import { SpeedSection } from "./SpeedParams";
import type { BackgroundRuns } from "./use-remove-background";
import classes from "./editor.module.css";

export interface InspectorProps {
  /** The project, for its AI assistant's chat (#242). */
  teamId: string;
  brandId: string;
  projectId: string;
  /** What the assistant reads and edits (#242 part 2). */
  assistant: AssistantEditor;
  timeline: Timeline;
  media: ReadonlyMap<string, SourceMedia>;
  selection: Selection | null;
  /** The member may not change this project: every field is shown, none can be changed. */
  readOnly: boolean;
  /** The project's frame: a picture's Transform is in live's pixels on it. */
  aspectRatio: AspectRatio;
  /**
   * The playhead's store: a sound's Volume keyframes are set at its time (spec volume-keyframes),
   * which the Volume section alone reads, so a seek renders it and not the panel (spec seek-renders.md).
   */
  playhead: Playhead;
  onSeek: (ms: number) => void;
  /** Remove background's runs, one a clip (spec remove-background.md). */
  background: BackgroundRuns;
  onEdit: (edit: TimelineEdit) => void;
  /**
   * On a phone (spec phone-layout.md), the sheet it shows, its tab list hidden. Null while its
   * sheet is shut; absent on the desktop.
   */
  phoneTab?: Tab | null | undefined;
  /** A count the phone's Speed action raises: each new one brings the Speed section into view. */
  speedShown?: number;
}

/** The right panel's tabs, live's: the AI assistant first, then the selection's parameters. */
export type Tab = "assistant" | "parameters";

/**
 * The right panel (live's): its tabs, "AI assistant" (its dot green while the team's events
 * arrive, red when not, live's connection dot) and "Parameters", with live's Reset chat at the end
 * of the row while the assistant is open. As live's, it opens on the assistant's chat (#242),
 * and selecting a clip, a title or a transition turns to "Parameters", the selection's settings.
 * Parameters shows only while something is chosen (issue 351): with nothing chosen, the assistant
 * alone, live's. A selection with a caption (#239) has no Parameters either: live hides the tab
 * and keeps the assistant.
 */
export function Inspector(props: InspectorProps) {
  const [tab, setTab] = useState<Tab>("assistant");
  const captioned = props.timeline.clips.some(
    (clip) => clip.kind === "caption" && (props.selection?.ids ?? []).includes(clip.id),
  );
  // A new selection turns to its parameters, as live does when an item is selected.
  const key = props.selection === null ? null : props.selection.ids.join(",");
  const [shown, setShown] = useState(key);
  if (key !== shown) {
    setShown(key);
    // A phone's open sheet keeps its tab: it says which.
    if (key !== null && (props.phoneTab ?? null) === null) setTab(captioned ? "assistant" : "parameters");
  }
  const [sheet, setSheet] = useState(props.phoneTab ?? null);
  if ((props.phoneTab ?? null) !== sheet) {
    setSheet(props.phoneTab ?? null);
    if (props.phoneTab !== undefined && props.phoneTab !== null) setTab(props.phoneTab);
  }
  const parameters = props.selection !== null && !captioned;
  const showing: Tab = parameters ? tab : "assistant";
  const tabId = useId();
  // Here, so the chat is kept while Parameters is open, as live keeps its assistant mounted.
  const project = { teamId: props.teamId, brandId: props.brandId, projectId: props.projectId };
  const chat = useAssistChat(project);
  const assistant = useAssistant(project, chat, props.assistant);
  const connected = useStreamStatus() === "live";
  const [resetting, setResetting] = useState(false);
  // Live's Lock: a locked clip's parameters change only once it is unlocked; with several
  // chosen, any locked one dims them all, as live's panel does.
  const locked = props.timeline.clips.some(
    (clip) => (props.selection?.ids ?? []).includes(clip.id) && clip.locked,
  );
  const lockKeys = onMac(navigator.userAgent) ? "⌘L" : "Ctrl+L";
  const tabOf = (name: Tab, label: ReactNode) => (
    <button
      type="button"
      role="tab"
      id={`${tabId}-${name}`}
      aria-selected={showing === name}
      aria-controls={`${tabId}-panel`}
      data-active={showing === name || undefined}
      className={classes.sideTab}
      onClick={() => setTab(name)}
    >
      {label}
    </button>
  );
  return (
    <aside className={classes.side} aria-label="Right panel" data-phone={props.phoneTab !== undefined || undefined}>
      <div className={classes.sideTabs}>
        <div role="tablist" aria-label="Panels" className={classes.sideTabList}>
          {tabOf(
            "assistant",
            <>
              <span className={classes.sideTabLabel}>AI assistant</span>
              <span className={classes.liveDot} data-connected={connected} aria-hidden />
            </>,
          )}
          {parameters ? tabOf("parameters", "Parameters") : null}
        </div>
        {showing === "assistant" ? (
          <Tooltip label="Reset chat">
            <ActionIcon
              variant="subtle"
              color="gray"
              size={28}
              aria-label="Reset chat"
              disabled={props.readOnly}
              onClick={() => {
                chat.resetForget();
                setResetting(true);
              }}
            >
              <RefreshCw size={14} />
            </ActionIcon>
          </Tooltip>
        ) : null}
      </div>
      <div
        role="tabpanel"
        id={`${tabId}-panel`}
        aria-labelledby={`${tabId}-${showing}`}
        className={classes.sidePanel}
      >
        {showing === "assistant" ? (
          <AssistantPanel chat={chat} assistant={assistant} readOnly={props.readOnly} />
        ) : (
          <>
            {locked ? (
              <Text className={classes.lockedNote} role="note">
                <Lock size={12} aria-hidden /> Locked: unlock it to change it ({lockKeys}, or the
                timeline's menu).
              </Text>
            ) : null}
            {/* Live dims a locked clip's panel; here its fields take no change either. */}
            <fieldset
              disabled={props.readOnly || locked}
              className={classes.fieldset}
              data-locked={locked || undefined}
            >
              <Settings {...props} />
            </fieldset>
          </>
        )}
      </div>
      <ConfirmModal
        opened={resetting}
        title="Reset chat?"
        confirmLabel="Confirm"
        onConfirm={() => chat.reset(() => setResetting(false))}
        onCancel={() => setResetting(false)}
        pending={chat.resetting}
        error={chat.resetError === null ? null : <ErrorAlert error={chat.resetError} />}
      >
        <Text>
          Are you sure you want to reset the chat? If you confirm, all conversation history will be
          deleted.
        </Text>
      </ConfirmModal>
    </aside>
  );
}

/**
 * A setting changed for the clip shown, made for every chosen clip of its kind (live's panel sets
 * all of them), the Volume slider's too, each clip at the playhead; any other edit (a move, a trim,
 * a removal, a keyframe's diamond) is the shown clip's alone.
 */
function forEvery(ids: readonly string[], onEdit: (edit: TimelineEdit) => void) {
  return (edit: TimelineEdit) => {
    if ((edit.type !== "setClip" && edit.type !== "setVolumeAt") || ids.length < 2) {
      onEdit(edit);
      return;
    }
    onEdit({ type: "many", edits: ids.map((id) => ({ ...edit, id })) });
  };
}

function Settings({ timeline, media, selection, aspectRatio, playhead, onSeek, background, speedShown, onEdit: editShown }: InspectorProps) {
  // A transition is chosen alone (#240): live's Transition Settings.
  const cut = junctionsOf(timeline).find((each) => each.transition?.id === selection?.id);
  if (cut?.transition != null) {
    return <TransitionSettings transition={cut.transition} junction={cut} onEdit={editShown} />;
  }
  const clip = timeline.clips.find((each) => each.id === selection?.id);
  const chosen = timeline.clips.filter((each) => (selection?.ids ?? []).includes(each.id));
  // Live's mixed selection: its panel shows only "Group".
  if (chosen.length > 1 && chosen.some((each) => each.kind !== chosen[0]!.kind)) {
    return (
      <section className={classes.inspector} aria-label="Group settings">
        <Text className={classes.groupTitle}>Group</Text>
      </section>
    );
  }
  const onEdit = forEvery(
    chosen.map((each) => each.id),
    editShown,
  );
  if (clip?.kind === "text") return <TextSettings text={clip} onEdit={onEdit} />;
  if (clip?.kind === "effect") return <EffectSettings effect={clip} onEdit={onEdit} />;
  // A caption has no Parameters (live's: its look is the Captions tab's, #264).
  if (clip?.kind === "caption") return null;
  if (clip !== undefined) {
    const source = media.get(sourceKey(clip.source));
    const number = clipsOn(timeline, clip.trackId).indexOf(clip) + 1;
    return (
      <ClipSettings
        clip={clip}
        name={source?.name ?? `Clip ${number}`}
        source={source}
        sourceMs={source?.durationMs ?? null}
        number={number}
        frame={liveFrame(aspectRatio)}
        timeline={timeline}
        playhead={playhead}
        onSeek={onSeek}
        background={background}
        speedShown={speedShown}
        onEdit={onEdit}
      />
    );
  }
  return (
    <section className={classes.inspector} aria-label="Settings">
      <Text size="sm" c="var(--cai-color-text-muted)">
        Select a clip or a text on the timeline or the canvas to change it.
      </Text>
    </section>
  );
}

interface ClipSettingsProps {
  clip: MediaClip;
  name: string;
  source: SourceMedia | undefined;
  /** Live's frame (`LIVE_FRAME`): a Transform's numbers are its pixels. */
  frame: Size;
  /** The source's length, the bound for the out point; `null` until it is known. */
  sourceMs: number | null;
  /** Its place on its track, for its name. */
  number: number;
  /** The timeline, for the Speed section's refusals (spec speed-section). */
  timeline: Timeline;
  playhead: Playhead;
  onSeek: (ms: number) => void;
  background: BackgroundRuns;
  speedShown?: number | undefined;
  onEdit: (edit: TimelineEdit) => void;
}

/**
 * Live's slider of a clip's sound, here to 200 % (spec volume-boost), its keyframes at the playhead
 * (spec volume-keyframes): the diamond adds or takes off the one there, between the previous and the
 * next; and its fades in milliseconds.
 */
function SoundSections(props: {
  clip: VideoClip | AudioClip;
  playhead: Playhead;
  onSeek: (ms: number) => void;
  onEdit: (edit: TimelineEdit) => void;
}) {
  const { clip, onSeek, onEdit } = props;
  // The one part of the panel that renders with the playhead (spec seek-renders.md).
  const playheadMs = usePlayhead(props.playhead);
  const set = (patch: ClipPatch) => onEdit({ type: "setClip", id: clip.id, patch });
  const longest = Math.min(MAX_FADE_MS, clipMs(clip));
  // With keyframes the slider is the level at the playhead, and takes a change only on the clip.
  const keyed = clip.volumePoints.length > 0;
  const onClip = playheadMs >= clip.startMs && playheadMs <= clip.endMs;
  const level = keyed ? volumeAt(clip, playheadMs) : clip.volume;
  const onPoint = onClip && volumePointNear(clip, playheadMs) !== undefined;
  const near = volumeNeighbours(clip, playheadMs);
  const before = near.before === undefined ? undefined : Math.round(near.before);
  const after = near.after === undefined ? undefined : Math.round(near.after);
  return (
    <>
      <ParamSection title="Volume">
        <LevelSlider
          label="Volume"
          bare
          value={Math.round(level * 100)}
          max={MAX_VOLUME * 100}
          marks={[100]}
          warn={level > 1}
          disabled={keyed && !onClip}
          unit="%"
          onChange={(percent) => onEdit({ type: "setVolumeAt", id: clip.id, atMs: playheadMs, volume: percent / 100 })}
        />
        {level > 1 ? <Text className={classes.levelWarning}>Above 100 % may distort</Text> : null}
        <div className={classes.keyframeRow}>
          <ActionIcon variant="subtle" size="sm" aria-label="Previous keyframe" disabled={before === undefined} onClick={() => before !== undefined && onSeek(before)}>
            <ChevronLeft size={14} aria-hidden />
          </ActionIcon>
          <ActionIcon
            variant={onPoint ? "light" : "subtle"}
            size="sm"
            aria-label={onPoint ? "Remove keyframe" : "Add keyframe"}
            aria-pressed={onPoint}
            disabled={!onClip}
            onClick={() => onEdit({ type: onPoint ? "removeVolumePoint" : "addVolumePoint", id: clip.id, atMs: playheadMs })}
          >
            <Diamond size={13} fill={onPoint ? "currentColor" : "none"} aria-hidden />
          </ActionIcon>
          <ActionIcon variant="subtle" size="sm" aria-label="Next keyframe" disabled={after === undefined} onClick={() => after !== undefined && onSeek(after)}>
            <ChevronRight size={14} aria-hidden />
          </ActionIcon>
        </div>
      </ParamSection>
      <ParamSection title="Fade">
        <LevelSlider
          label="Fade-in"
          value={clip.fadeInMs}
          max={longest}
          unit="ms"
          onChange={(fadeInMs) => set({ fadeInMs })}
        />
        <LevelSlider
          label="Fade-out"
          value={clip.fadeOutMs}
          max={longest}
          unit="ms"
          onChange={(fadeOutMs) => set({ fadeOutMs })}
        />
      </ParamSection>
    </>
  );
}

function ClipSettings(props: ClipSettingsProps) {
  const { clip, name, source, sourceMs, number, frame, playhead, onSeek, background, onEdit } = props;
  const picture = usePictureSize(source);
  const id = clip.id;
  const noun = clip.kind === "audio" ? "Sound" : "Clip";
  const sourced = clip.kind === "video" || clip.kind === "audio" ? clip : null;
  // The file's out-point: a clip at 2x reads twice its length (spec clip-speed).
  const outMs = sourced === null ? 0 : sourced.inMs + Math.round(clipMs(clip) * sourced.speed);
  return (
    <section className={classes.inspector} aria-label={`${noun} settings`}>
      <div className={classes.paramHead}>
        <Text className={classes.paramTitle} truncate>
          {noun} {number}: {name}
        </Text>
        <Text className={classes.paramNote}>
          {sourced === null
            ? `A still, ${formatClock(clipMs(clip))}`
            : sourceMs === null
              ? `Source length loading, using ${formatClock(clipMs(clip))}`
              : `Source ${formatClock(sourceMs)}, using ${formatClock(clipMs(clip))}`}
        </Text>
      </div>
      <ParamSection title="Timing">
        <SecondsField
          label="Start"
          ms={clip.startMs}
          min={0}
          onCommit={(startMs) => onEdit({ type: "moveClip", id, startMs })}
        />
        {sourced === null ? (
          <SecondsField
            label="End"
            ms={clip.endMs}
            min={clip.startMs + MIN_SPAN_MS}
            onCommit={(endMs) => onEdit({ type: "trimEnd", id, endMs, sourceMs: null })}
          />
        ) : null}
      </ParamSection>
      {sourced === null ? null : (
        <ParamSection title="Trim">
          <SecondsField
            label="In"
            ms={sourced.inMs}
            min={0}
            max={outMs - MIN_SPAN_MS}
            onCommit={(inMs) => onEdit({ type: "setRange", id, inMs, outMs, sourceMs })}
          />
          <SecondsField
            label="Out"
            ms={outMs}
            min={sourced.inMs + MIN_SPAN_MS}
            {...(sourceMs === null ? {} : { max: sourceMs })}
            onCommit={(next) =>
              onEdit({ type: "setRange", id, inMs: sourced.inMs, outMs: next, sourceMs })
            }
          />
        </ParamSection>
      )}
      {sourced === null ? null : (
        <SpeedSection clip={sourced} timeline={props.timeline} playhead={props.playhead} shown={props.speedShown} onEdit={onEdit} />
      )}
      {clip.kind === "audio" ? null : (
        <LookSections
          clip={clip}
          frame={frame}
          picture={picture}
          background={
            clip.kind === "video" ? (
              <BackgroundSection
                clip={clip}
                run={background.runOf(id)}
                onRemove={() => background.remove(clip)}
                onRestore={() => onEdit({ type: "restoreOriginal", id })}
              />
            ) : undefined
          }
          onEdit={onEdit}
        />
      )}
      {clip.kind === "video" ? (
        <ParamSection title="Audio">
          <Switch
            label="Mute this clip"
            labelPosition="left"
            classNames={{ body: classes.paramSwitch, label: classes.paramLabel }}
            checked={clip.muted}
            onChange={(event) =>
              onEdit({ type: "setClip", id, patch: { muted: event.currentTarget.checked } })
            }
          />
        </ParamSection>
      ) : null}
      {sourced === null ? null : <SoundSections clip={sourced} playhead={playhead} onSeek={onSeek} onEdit={onEdit} />}
      {/* A sound's beats (spec find-beats.md), a find of its own for each sound. */}
      {clip.kind === "audio" ? (
        <BeatsSection key={clip.id} clip={clip} url={source?.url ?? null} sourceMs={sourceMs} onEdit={onEdit} />
      ) : null}
      <Button
        size="xs"
        variant="subtle"
        color="red"
        mt="md"
        leftSection={<Trash2 size={14} />}
        onClick={() => onEdit({ type: "removeClip", id })}
      >
        Remove {noun.toLowerCase()}
      </Button>
    </section>
  );
}
